import { adminApi } from './api';

/* ==========================================
  교육과정 관리 (Course Management)
========================================== */

/**
 * 교육과정 목록 조회
 * @param {Object} params - { page, size, freeYn, languageId, courseType, hasContent }
 * @param {number} params.page - 페이지 번호 (1부터 시작)
 */
export const fetchCourseList = async (params) => {
  const { search, ...restParams } = params;
  const response = await adminApi.get(`/api/admin/courses`, { params: restParams });
  return response.data.data;
};

/* 교육과정 상세 조회 */
export const fetchCourseDetail = async (courseId) => {
  const response = await adminApi.get(`/api/admin/courses/${courseId}`);
  return response.data.data;
};

/**
 * 교육과정 추가 (Multipart)
 * @param {Object} courseData - 필드 데이터와 File 객체가 포함된 객체
 */
export const createCourse = async (courseData) => {
  const formData = new FormData();

  // 1. 일반 필드를 JSON 객체로 만들어서 "courseCreate"로 전송
  const courseCreateData = {
    category: courseData.category,
    courseCount: courseData.courseCount,
    courseName: courseData.courseName,
    courseObj: courseData.courseObj,
    hasContent: courseData.hasContent,
    languageId: courseData.languageId,
    freeYn: courseData.freeYn,
    hasTraining: courseData.hasTraining,
    instId: courseData.instId,
    courseInstPage: courseData.courseInstPage,
  };

  // undefined/null 값 제거
  Object.keys(courseCreateData).forEach(key => {
    if (courseCreateData[key] === undefined || courseCreateData[key] === null) {
      delete courseCreateData[key];
    }
  });

  // JSON을 Blob으로 변환하여 FormData에 추가
  formData.append('courseCreate', new Blob([JSON.stringify(courseCreateData)], { type: 'application/json' }));

  // 2. 파일 필드 추가 (백엔드 필드명에 맞춤)
  if (courseData.thumbnailImg) {
    formData.append('thumbnailImg', courseData.thumbnailImg);
  }
  if (courseData.introImg) {
    formData.append('introImg', courseData.introImg);
  }
  if (courseData.lessonPlanFile) {
    formData.append('lessonPlanFile', courseData.lessonPlanFile);
  }

  const response = await adminApi.post(`/api/admin/courses`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  // Location 헤더에서 courseId 추출을 위해 response 전체 반환
  return response;
};

/**
 * 교육과정 변경 (Multipart)
 * @param {Object} courseData - courseId 포함
 */
export const updateCourse = async (courseData) => {
  const formData = new FormData();

  // 1. 일반 필드를 JSON 객체로 만들어서 "courseUpdate"로 전송
  const courseUpdateData = {
    courseId: courseData.courseId,
    category: courseData.category,
    courseCount: courseData.courseCount,
    courseName: courseData.courseName,
    courseObj: courseData.courseObj,
    hasContent: courseData.hasContent,
    languageId: courseData.languageId,
    freeYn: courseData.freeYn,
    hasTraining: courseData.hasTraining,
    instId: courseData.instId,
    courseInstPage: courseData.courseInstPage,
  };

  // undefined/null 값 제거
  Object.keys(courseUpdateData).forEach(key => {
    if (courseUpdateData[key] === undefined || courseUpdateData[key] === null) {
      delete courseUpdateData[key];
    }
  });

  // JSON을 Blob으로 변환하여 FormData에 추가
  formData.append('courseUpdate', new Blob([JSON.stringify(courseUpdateData)], { type: 'application/json' }));

  // 2. 파일 필드 추가 (파일이 새로 업로드된 경우에만 append, 백엔드 필드명에 맞춤)
  if (courseData.thumbnailImg instanceof File) {
    formData.append('thumbnailImg', courseData.thumbnailImg);
  }
  if (courseData.introImg instanceof File) {
    formData.append('introImg', courseData.introImg);
  }
  if (courseData.lessonPlanFile instanceof File) {
    formData.append('lessonPlanFile', courseData.lessonPlanFile);
  }

  const response = await adminApi.put(`/api/admin/courses`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return response.data;
};

/**
 * 교육과정 폐강 (soft delete).
 * 영상·수강 기록·미디버스 미디어는 모두 보존되며 되돌릴 수 있다.
 * 진행 중인 그룹에 편성된 과정이면 409로 거부되고, force=true로 재요청하면 폐강된다.
 */
export const deleteCourse = async (courseId, force = false) => {
  const response = await adminApi.delete(`/api/admin/courses/${courseId}`, {
    params: force ? { force: true } : undefined,
  });
  return response.data.data;
};

/* 교육과정 폐강 취소 (복구) */
export const restoreCourse = async (courseId) => {
  const response = await adminApi.post(`/api/admin/courses/${courseId}/restore`);
  return response.data.data;
};

/**
 * 과정 등록 직후 실패 시 롤백용 삭제.
 * 정식 삭제(deleteCourse)는 폐강(soft delete)이라 껍데기 코스가 남으므로,
 * 롤백은 물리 삭제 엔드포인트를 사용한다. 수강 기록이 없는 갓 생성된 코스라 안전하다.
 */
export const rollbackFailedCourseCreation = async (courseId) => {
  const response = await adminApi.delete(`/api/admin/courses/${courseId}/hard`);
  return response.data.data;
};

/* 교육과정 상태 변경 (hasContent 토글) */
export const updateCourseStatus = async (courseId, hasContent) => {
  // hasContent: 0, 1
  const response = await adminApi.patch(`/api/admin/courses/${courseId}/publish`, { courseId, hasContent });
  return response.data.data;
};